import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AppServices } from '../../../AppServices';
import { ApplicationState } from '../../../store';
import * as SharedMaterialsPreviewStateController from '../../../store/StateControllers/SharedMaterials/SharedMaterialsPreviewStateController';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import * as LoginStateController from '../../../store/StateControllers/LoginStateController';
import Layout from '../../Layout';
import { AbstractSharedMaterialDetail, DetailSectionRenderData, PublishMode } from './AbstractSharedMaterialDetail';
import { TempPublishTowState } from '../../TOW';
import { SharedMaterialAccessType } from '../../../store/Models/SharedMaterials/DataModel';

import './SharedMaterialDetail.css';
import './SharedMaterialPublishPreview.css';

type SharedMaterialPublishPreviewProps =
    { appServices: AppServices } &
    typeof LoginStateController.actionCreators &
    { sharedMaterialsPreviewState: SharedMaterialsPreviewStateController.SharedMaterialPreviewState } &
    ToxnotComponentProperties &
    typeof SharedMaterialsPreviewStateController.actionCreators &
    RouteComponentProps<{ publicInventoryId: string, publicInventoryDataId: string, mode?: PublishMode }>

class SharedMaterialPublishPreview extends AbstractSharedMaterialDetail<SharedMaterialPublishPreviewProps> {
    
    // This method is called when the component is first added to the document
    public componentDidMount() {
        const params = this.props.match.params;
        this.props.loadSharedMaterialPreviewBundle(
            params.publicInventoryId,
            params.publicInventoryDataId,
            params.mode === PublishMode.Multiple
        );
    }

    // ===============================================================================

    protected renderModals() {
        return (
            <>
            </>
        )
    }

    // ===============================================================================

    protected isLoading() {
        return this.props.sharedMaterialsPreviewState?.isLoading ?? true;
    }

    // ===============================================================================

    protected peekRenderData(): DetailSectionRenderData {
        const bundle = this.props.sharedMaterialsPreviewState.previewBundle!;

        const coreData = bundle.coreData;

        coreData.hasAccess = !this.props.sharedMaterialsPreviewState.renderWithoutAccess;

        return {
            coreData: coreData,
            detailBundle: bundle.detailResponse,
            company: bundle.company,
        };
    }

    // ===============================================================================

    protected peekCollapsedSections(): string[] {
        return this.props.sharedMaterialsPreviewState.collapsedSections;
    }

    // ===============================================================================

    private renderAccessLevelConsole(): JSX.Element | null {
        const state = this.props.sharedMaterialsPreviewState;

        if (state.previewBundle === null) {
            return null;
        }

        const context = state.previewBundle.coreData.accessLevel == SharedMaterialAccessType.Protected
            ? this.localizeString("View this RESTRICTED Passport as a User that does NOT have permission would")
            : this.localizeString("View this UNLISTED Passport as a User that does NOT have permission would");
        return (
            <div className="tn-section passport-publish-preview-disclaimer-section">
                <div className="checkbox">
                    <label>
                        <input type="checkbox" checked={state.renderWithoutAccess} onChange={this.renderWithoutAccess_change} />
                        {context}
                    </label>
                </div>
            </div>
        );
    }

    // ===============================================================================

    /**
     * Overrides to apply the layout. Also optionally show a console to let the user
     * appreciate how a non-Public Passport will be rendered to those without access.
     */
    protected wrapBody(body: JSX.Element): JSX.Element {
        let showAccessBar = false;
        if (!this.isLoading()) {
            const state = this.props.sharedMaterialsPreviewState;
            const accessLevel = state.previewBundle!.coreData.accessLevel;
            showAccessBar = accessLevel !== SharedMaterialAccessType.Public;
            body = accessLevel !== SharedMaterialAccessType.Private || !state.renderWithoutAccess ? body :
                (<div className="tn-section"><i>{this.localizeString("An Unlisted Passport will not appear in the Passport Library for a User unless they have access.")}</i></div>);
        }

        return (
            <Layout>
                {showAccessBar && this.renderAccessLevelConsole()}
                {body}
            </Layout>
        );
    }

    /**
     * If preview is for non public, show a toggle that allows showing the restricted view.
     */
    protected renderTopLevelActionButtons(data: DetailSectionRenderData) {
        return null;
    }

    // ===============================================================================

    renderViewDetailsAttributes = (inventoryId: string, publicInventoryId: string, publicInventoryDataId: string, startId: string, startContext: string) => {
        return {
            role: "button",
            onClick: (e: React.MouseEvent) => {
                this.viewDetails_click(e, startId, startContext);
            }
        };
    }

    // ===============================================================================
    // This method is implemented to fulfill the abstract method requirement from the interface "AbstractSharedMaterialDetail".
    public addToLibraryButton_click = (sharedMaterialId: string): void => { };

    // ===============================================================================

    private doMultipleModeViewDetails() {
        window.location.href = `/Data/Inventory?publicInventoryId=${encodeURIComponent(this.props.match.params.publicInventoryId)}&publicInventoryDataId=${encodeURIComponent(this.props.match.params.publicInventoryDataId)}&mode=PassportPreview&accessLevel=${encodeURIComponent(this.props.sharedMaterialsPreviewState.previewBundle!.coreData.accessLevel)}`;
    }

    // ===============================================================================

    private doSingleModeViewDetails(startId: string, startContext: string) {
        if (window.confirm("Once published, this will open the full Material view in a new tab. As this is a preview, do you want to advance to the Preview Inventory step?")) {
            const towState = this.props.appServices.towState!;
            const state = JSON.parse(towState.jsonState) as TempPublishTowState;
            state.activeStep = "PreviewDeep";
            state.previewStartId = startId;
            state.previewStartContext = startContext;
            towState.jsonState = JSON.stringify(state);
            this.props.towTaskUpdate(towState);
        }
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private renderWithoutAccess_change = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.toggleRenderWithoutAccess(e.currentTarget.checked);
    }

    private viewDetails_click = (e: React.MouseEvent, startId: string, startContext: string) => {
        // short circuit the href
        e.preventDefault();

        if (this.props.match.params.mode === PublishMode.Multiple) {
            this.doMultipleModeViewDetails();
        }
        else {
            this.doSingleModeViewDetails(startId, startContext);
        }
    }

    // ===============================================================================

    protected toggleSection_click = (dataSectionId: string) => {
        this.props.toggleDetailSectionCollapse(dataSectionId);
    }

    // ===============================================================================

    protected contactSupplierButton_click = (e: React.MouseEvent) => {
        alert(this.localizeString("Once this Material is published, pressing this allows an interested party to send you an email"));
        e.preventDefault();
    }

    // ===============================================================================

    protected viewCompanyButton_click = (e: React.MouseEvent) => {
        alert(this.localizeString("Once this Material is published, pressing this will view your Company page"));
        e.preventDefault();
    }

    // ===============================================================================

    protected purchaseMaterial_click = (e: React.MouseEvent) => {
        alert(this.localizeString("Once this Material is published, pressing this will view the URL to purchase this Material"));
        e.preventDefault();
    }

    // ===============================================================================

    protected requestAccessButton_click = (e: React.MouseEvent) => {
        alert(this.localizeString("Once this Material is published as Restricted or Unlisted, pressing this allows an interested party to request access"));
        e.preventDefault();
    }
}

export default connect(
    (state: ApplicationState) => ({
        sharedMaterialsPreviewState: state.sharedMaterialsPreviewState,
        appServices: state.appServices
    }),
    { ...LoginStateController.actionCreators, ...SharedMaterialsPreviewStateController.actionCreators },
)(SharedMaterialPublishPreview as any);
